/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {useCallback} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {isSCIDevice, normalizeSCIDevice} from '@ion/core/src/services/ArcGISUtils';
import {endOfDay, startOfDay} from '@ion/core/src/utils/DateUtil';
import {DateSelectorValues, TrackingMode} from '@ion/dls/src/types/types';
import {clearFilters} from '@/store/actions/reports';
import {
  launchSHR,
  setBoundaryFilter,
  setTrackingMode,
  viewAssetHistory as viewHistory
} from '@/store/actions/tracking';
import {selectedAssetSelector} from '@/store/selectors/tracking-selectors';
import {useApplyHistoryTimeMode} from './useApplyHistoryTimeMode';

/**
 * The useViewAssetHistory hook is used to view asset history of the selected asset.
 *
 * ```typescript
 *
 *   import {useViewAssetHistory} from './hooks/history/useViewHistory';
 *
 *   const viewHistory = useViewAssetHistory(selectedAsset);
 *
 *   <SomeComponent ...
 *    onClick={viewHistory}
 *    ... />
 *
 * ```
 */
export const useViewAssetHistory = () => {
  const dispatch             = useDispatch();
  const selectedAsset        = useSelector(selectedAssetSelector);
  const applyHistoryTimeMode = useApplyHistoryTimeMode();

  const viewAssetHistory = useCallback((asset) => {
    const historyAsset = asset || selectedAsset;
    const dateVal = new Date(historyAsset.getDevice?.()?.eventTime || historyAsset.eventTime);
    dispatch(clearFilters());
    dispatch(setBoundaryFilter([]));
    if (!isSCIDevice(historyAsset)) {
      // Asset History Report
      dispatch(viewHistory({selectedAsset: historyAsset, dateVal}) as any);
      dispatch(setTrackingMode(TrackingMode.History) as any);
      // in the current theme the asset's history is a History report like
      // any other, so the Time Mode control is moved to History over the
      // same day the report is run over ...
      applyHistoryTimeMode({
        preset : DateSelectorValues.Custom,
        from   : startOfDay(dateVal),
        to     : endOfDay(dateVal)
      });
    } else {
      // Gateway Report
      const date = new Date(historyAsset?.eventTime);
      dispatch(launchSHR({selectedDevice: normalizeSCIDevice(historyAsset), date}) as any);
    }
  }, [selectedAsset, applyHistoryTimeMode]);

  return viewAssetHistory;
};

export default useViewAssetHistory;
